import { useReactiveVar } from '@apollo/client'
import CircularProgress from '@material-ui/core/CircularProgress'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import clsx from 'clsx'
import {
    useContentItemFetcher,
    usePermissionChecks,
    useTreeHandlers,
} from 'components/WorkspaceEditor/hooks'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useContext } from 'react'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'
import type { DataNode, NodeType } from '../../types/types'

/** Props for rendering switcher icon of <TreeView /> component */

export interface SwitcherIconProps {
    /** The node type */
    type: NodeType
    /* A flag whether the node is loading */
    isLoading: boolean
    /* A flag whether the node is expanded */
    isExpanded: boolean
    'data-testid'?: string
    node: DataNode
}

/**
 * Renders the switcher icon property of <TreeView /> component
 * @param {SwitcherIconProps} props - Props for rendering switcher icon
 * @returns {ReactElement} Node switcher icon
 */
export const SwitcherIcon = memo<SwitcherIconProps>(
    ({
        type,
        isLoading,
        isExpanded,
        'data-testid': dataTestId = 'switcher-icon',
        node,
    }) => {
        const classes = useTreeViewStyles()

        const { treeData, setTreeData, expandedKeys } = useShallowSelector(
            (s) => ({
                treeData: s.treeData,
                setTreeData: s.actions.setTreeData,
                expandedKeys: s.expandedKeys,
            })
        )

        const {
            selectedElementNode: selectedProjectElementNode,
        } = useReactiveVar(workspaceSelectedElement)
        const permission = usePermissionChecks(
            treeData,
            selectedProjectElementNode
        )

        const { projectId } = useContext(ProjectPageContext)
        const {
            fetchContentItemView: fetchProductFamily,
        } = useContentItemFetcher({
            projectId: projectId!,
            treeData,
            setTreeData,
            permission,
        })

        const {
            handleMouseHover,
            handleMouseLeave,
            handleExpandCollapseArticle,
        } = useTreeHandlers(fetchProductFamily)

        // only Articles can have switcherIcon (expand/collapse)
        if (type !== NODE_TYPES.Article) {
            return (
                <div
                    onMouseOver={() => handleMouseHover(node)}
                    onMouseLeave={handleMouseLeave}
                    className={`${classes.hiddenContainer} leaf`}
                />
            )
        }

        if (isLoading) {
            return (
                <CircularProgress
                    data-testid={dataTestId}
                    className={classes.switcherLoadingIcon}
                    size={16}
                />
            )
        }

        return (
            <div
                onMouseOver={() => handleMouseHover(node)}
                onMouseLeave={handleMouseLeave}
                className={clsx('switcherIcon', classes.switcherIconContainer)}
            >
                {isExpanded ? (
                    <ExpandMoreIcon
                        onClick={() =>
                            handleExpandCollapseArticle(
                                expandedKeys,
                                false,
                                node
                            )
                        }
                        data-testid={dataTestId}
                        className={classes.switcherIcon}
                    />
                ) : (
                    <ChevronRightIcon
                        onClick={() =>
                            handleExpandCollapseArticle(
                                [...expandedKeys, node.key],
                                true,
                                node
                            )
                        }
                        data-testid={dataTestId}
                        className={classes.switcherIconCollapsed}
                    />
                )}
            </div>
        )
    }
)
